import { APP_LAUNCHER_APPS } from "./AppLauncherApps";
// src/components/SearchBar.ts
import "./SearchBar.css";
import { searchMap } from "../searchMap";
import { useAuth } from "../context/useAuth";
import { renderProfileDropdown } from "./ProfileDropdown";
import {
  renderSearchSuggestions,
  initSearchSuggestionsEvents,
} from "./SearchSuggestions";
import {
  AYE_INPUT_LANGUAGES,
  attachLanguageAltInput,
  getAyeInputLanguageMeta,
  openAyeLanguageTutorial,
  readAyeInputLanguage,
  writeAyeInputLanguage,
  type AyeInputLanguage,
} from "./LanguageAltInput";

function normalizeQuery(q: string): string {
  return q
    .toLowerCase()
    .replace(/\.(jpg|jpeg|png|gif|webp)$/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function getProfileColor(email: string): string {
  const colors = ["#4285f4", "#ea4335", "#fbbc05", "#34a853", "#a142f4"];

  let hash = 0;

  for (let i = 0; i < email.length; i += 1) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

function renderSearchLanguageControl(suffix: "desktop" | "mobile"): string {
  const current = readAyeInputLanguage();
  const meta = getAyeInputLanguageMeta(current);

  return `
    <div class="sb-language-control" data-language-control="${suffix}">
      <button
        class="sb-language-btn"
        type="button"
        data-language-button="${suffix}"
        aria-haspopup="menu"
        aria-expanded="false"
        aria-label="Search input language: ${meta.name}"
      >
        <span data-language-label="${suffix}">${meta.shortName}</span>
        <span class="sb-language-chevron" aria-hidden="true">▾</span>
      </button>

      <div
        class="sb-language-menu hidden"
        data-language-menu="${suffix}"
        role="menu"
      >
        <button
          class="sb-language-menu-tutorial"
          type="button"
          role="menuitem"
          data-language-menu-tutorial="${suffix}"
        >
          <span class="sb-language-menu-tutorial-icon" aria-hidden="true">?</span>
          <span>Typing Tutorial</span>
        </button>
        <div class="sb-language-menu-divider" aria-hidden="true"></div>
        ${AYE_INPUT_LANGUAGES.map(
          (language) => `
            <button
              class="sb-language-option ${language.id === current ? "active" : ""}"
              type="button"
              role="menuitemradio"
              aria-checked="${language.id === current ? "true" : "false"}"
              data-language-value="${language.id}"
            >
              <span>${language.name}</span>
              <span class="sb-language-option-short">${language.shortName}</span>
            </button>
          `,
        ).join("")}
      </div>
    </div>
  `;
}

export function renderSearchBar(
  container: HTMLElement,
  initialQuery: string = "",
): void {
  const currentPath = window.location.pathname.toLowerCase();

  const isImages =
    currentPath === "/search/images" || currentPath === "/search/images/";

  const isAll =
    currentPath === "/search" ||
    currentPath === "/search/" ||
    currentPath === "/";

  // Auth is already initialized by main.ts before initRouter().
  // SearchBar only reads the existing state.
  const { user } = useAuth();

  const isLoggedIn = Boolean(user?.email);
  const profileLetter = isLoggedIn ? user!.email[0].toUpperCase() : "";

  const profileColor = isLoggedIn ? getProfileColor(user!.email) : "";

  container.innerHTML = `
    <div class="searchbar-top desktop-only">
      <div class="searchbar-logo" id="sb-logo">Ayesearch</div>

      <div class="search-input-wrapper" id="sb-wrapper">
        <form class="searchbar-box" id="sb-form">
          <span class="search-box-left-icon">🔍</span>

          <input
            type="text"
            class="searchbar-input"
            id="sb-input"
            placeholder="Search AyeSearch..."
            value="${escapeAttribute(initialQuery)}"
            autocomplete="off"
          />

          <div class="searchbar-actions">
            ${renderSearchLanguageControl("desktop")}
            <button
              class="sb-language-tutorial-btn"
              type="button"
              data-language-tutorial="desktop"
              aria-label="Language typing tutorial"
              title="Language typing tutorial"
            >?</button>
            <div class="searchbar-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="#9aa0a6" viewBox="0 -960 960 960">
                <path d="M480-480q-33 0-56.5-23.5T400-560v-200q0-33 23.5-56.5T480-840q33 0 56.5 23.5T560-760v200q0 33-23.5 56.5T480-480Zm0 200q-83 0-141.5-58.5T280-480h80q0 50 35 85t85 35q50 0 85-35t35-85h80q0 83-58.5 141.5T480-280Zm-40 200v-120q0-17 11.5-28.5T480-240q17 0 28.5 11.5T520-200v120h-80Z"/>
              </svg>
            </div>
          </div>
        </form>

        <div id="sb-suggestions"></div>
      </div>

      <div class="searchbar-right">
        <div class="right-icon" id="sb-apps-btn">
          <svg height="22" width="22" fill="var(--text)" viewBox="0 0 24 24">
            <circle cx="5" cy="5" r="2.5" />
            <circle cx="12" cy="5" r="2.5" />
            <circle cx="19" cy="5" r="2.5" />
            <circle cx="5" cy="12" r="2.5" />
            <circle cx="12" cy="12" r="2.5" />
            <circle cx="19" cy="12" r="2.5" />
            <circle cx="5" cy="19" r="2.5" />
            <circle cx="12" cy="19" r="2.5" />
            <circle cx="19" cy="19" r="2.5" />
          </svg>
        </div>

        <div class="apps-panel hidden" id="sb-apps-panel">
          <div class="sb-apps-grid">
            ${APP_LAUNCHER_APPS.map(app => `
              <div class="sb-app-item">
                <img class="sb-app-icon" src="${app.image}" alt="${app.name}" />
                <span>${app.name}</span>
              </div>
            `).join("")}
          </div>
          <div class="sb-download-hub">
            <div class="sb-download-hub-header">
              <img
                class="sb-browser-icon"
                src="/static/branding/ayesearch-21a23f7ae5cb.png"
                alt="AyeSearch"
              />
              <div class="sb-browser-copy">
                <strong>Get AyeSearch</strong>
                <span>Mobile app or desktop browser.</span>
              </div>
            </div>

            <div class="sb-download-platforms">
              <div class="sb-download-platform">
                <span class="sb-download-kicker">Mobile app</span>
                <strong>iOS / Android</strong>
                <a
                  class="sb-browser-download-btn"
                  href="https://ayesearch.com/downloads/update"
                  aria-label="Download AyeSearch for iOS or Android"
                >Download</a>
              </div>

              <div class="sb-download-platform">
                <span class="sb-download-kicker">AyeSearch Browser</span>
                <strong>Mac / Windows</strong>
                <a
                  class="sb-browser-download-btn"
                  href="https://ayesearch.com/downloads/update"
                  aria-label="Download AyeSearch Browser for Mac or Windows"
                >Download</a>
              </div>
            </div>
          </div>
        </div>

        ${
          isLoggedIn
            ? `<div
                class="profile-icon"
                id="sb-profile-icon"
                style="background-color:${profileColor}"
                role="button"
                tabindex="0"
                aria-label="Open profile"
              >${profileLetter}</div>`
            : `<button
                class="searchbar-signin-btn"
                id="sb-signin-btn"
                type="button"
              >Sign In</button>`
        }
      </div>
    </div>

    <div class="mobile-version">
      <div class="mobile-top-bar">
        <div class="mobile-hamburger">☰</div>
        <div class="mobile-logo" id="sb-mobile-logo">Ayesearch</div>

        ${
          isLoggedIn
            ? `<div
                class="mobile-profile"
                id="sb-mobile-profile-icon"
                style="background-color:${profileColor}"
                role="button"
                tabindex="0"
                aria-label="Open profile"
              >${profileLetter}</div>`
            : `<button
                class="searchbar-signin-btn mobile-searchbar-signin"
                id="sb-mobile-signin-btn"
                type="button"
              >Sign In</button>`
        }
      </div>

      <div class="mobile-search-row">
        <form class="searchbar-box" id="sb-form-mobile">
          <span class="search-box-left-icon">🔍</span>

          <input
            type="text"
            class="searchbar-input"
            id="sb-input-mobile"
            placeholder="Search AyeSearch..."
            value="${escapeAttribute(initialQuery)}"
            autocomplete="off"
          />

          ${renderSearchLanguageControl("mobile")}
          <button
            class="sb-language-tutorial-btn"
            type="button"
            data-language-tutorial="mobile"
            aria-label="Language typing tutorial"
            title="Language typing tutorial"
          >?</button>
          <span class="mobile-mic-icon">
            <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="#9aa0a6" viewBox="0 -960 960 960">
              <path d="M480-480q-33 0-56.5-23.5T400-560v-200q0-33 23.5-56.5T480-840q33 0 56.5 23.5T560-760v200q0 33-23.5 56.5T480-480Zm0 200q-83 0-141.5-58.5T280-480h80q0 50 35 85t85 35q50 0 85-35t35-85h80q0 83-58.5 141.5T480-280Zm-40 200v-120q0-17 11.5-28.5T480-240q17 0 28.5 11.5T520-200v120h-80Z"/>
            </svg>
          </span>
        </form>
      </div>
    </div>

    <div class="searchbar-categories">
      <button
        class="cat-btn ${currentPath.includes("/aizone") ? "active" : ""}"
        data-category="aizone"
      >
        AI Zone
      </button>

      <button
        class="cat-btn ${isAll ? "active" : ""}"
        data-category="all"
      >
        All
      </button>

      <button
        class="cat-btn ${isImages ? "active" : ""}"
        data-category="images"
      >
        Images
      </button>

      <button
        class="cat-btn ${currentPath.includes("/news") ? "active" : ""}"
        data-category="news"
      >
        News
      </button>

      <button
        class="cat-btn ${currentPath.includes("/videos") ? "active" : ""}"
        data-category="videos"
      >
        Videos
      </button>

      <button
        class="cat-btn ${currentPath.includes("/vibejoos") ? "active" : ""}"
        data-category="vibejoos"
      >
        VibeJoos
      </button>

      <button
        class="cat-btn ${currentPath.includes("/shopping") ? "active" : ""}"
        data-category="shopping"
      >
        Shopping
      </button>

      <button
        class="cat-btn ${currentPath.includes("/tools") ? "active" : ""}"
        data-category="tools"
      >
        Tools
      </button>
    </div>
  `;

  initSearchBarEvents(container);
}

function initSearchBarEvents(container: HTMLElement): void {
  const input = container.querySelector("#sb-input") as HTMLInputElement | null;

  const inputMobile = container.querySelector(
    "#sb-input-mobile",
  ) as HTMLInputElement | null;

  const form = container.querySelector("#sb-form") as HTMLFormElement | null;

  const formMobile = container.querySelector(
    "#sb-form-mobile",
  ) as HTMLFormElement | null;

  if (input) {
    attachLanguageAltInput(input, {
      getLanguage: readAyeInputLanguage,
    });
  }

  if (inputMobile) {
    attachLanguageAltInput(inputMobile, {
      getLanguage: readAyeInputLanguage,
    });
  }

  const suggestionsBox = container.querySelector(
    "#sb-suggestions",
  ) as HTMLElement | null;

  const wrapper = container.querySelector("#sb-wrapper") as HTMLElement | null;

  const appsBtn = container.querySelector("#sb-apps-btn") as HTMLElement | null;

  const appsPanel = container.querySelector(
    "#sb-apps-panel",
  ) as HTMLElement | null;

  const profileIcon = container.querySelector(
    "#sb-profile-icon",
  ) as HTMLElement | null;

  const mobileProfileIcon = container.querySelector(
    "#sb-mobile-profile-icon",
  ) as HTMLElement | null;

  const signInBtn = container.querySelector(
    "#sb-signin-btn",
  ) as HTMLButtonElement | null;

  const mobileSignInBtn = container.querySelector(
    "#sb-mobile-signin-btn",
  ) as HTMLButtonElement | null;

  const languageControls = container.querySelectorAll<HTMLElement>(
    ".sb-language-control",
  );

  const closeLanguageMenus = () => {
    languageControls.forEach((control) => {
      control
        .querySelector<HTMLElement>(".sb-language-menu")
        ?.classList.add("hidden");
      control
        .querySelector<HTMLButtonElement>(".sb-language-btn")
        ?.setAttribute("aria-expanded", "false");
    });
  };

  const updateLanguageControls = (language: AyeInputLanguage) => {
    const selectedMeta = getAyeInputLanguageMeta(language);

    languageControls.forEach((control) => {
      const label = control.querySelector<HTMLElement>("[data-language-label]");
      const button = control.querySelector<HTMLButtonElement>(".sb-language-btn");

      if (label) label.textContent = selectedMeta.shortName;
      if (button) button.setAttribute("aria-label", `Search input language: ${selectedMeta.name}`);

      control
        .querySelectorAll<HTMLButtonElement>(".sb-language-option")
        .forEach((option) => {
          const active = option.dataset.languageValue === language;
          option.classList.toggle("active", active);
          option.setAttribute("aria-checked", String(active));
        });
    });
  };

  languageControls.forEach((control) => {
    const button = control.querySelector<HTMLButtonElement>(".sb-language-btn");
    const menu = control.querySelector<HTMLElement>(".sb-language-menu");

    button?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const shouldOpen = menu?.classList.contains("hidden") ?? false;
      closeLanguageMenus();

      if (shouldOpen) {
        menu?.classList.remove("hidden");
        button.setAttribute("aria-expanded", "true");
      }
    });

    control
      .querySelector<HTMLButtonElement>(".sb-language-menu-tutorial")
      ?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeLanguageMenus();
        openAyeLanguageTutorial(readAyeInputLanguage());
      });

    control
      .querySelectorAll<HTMLButtonElement>(".sb-language-option")
      .forEach((option) => {
        option.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          const language = option.dataset.languageValue as AyeInputLanguage | undefined;
          if (!language) return;

          writeAyeInputLanguage(language);
          updateLanguageControls(language);
          closeLanguageMenus();

          const activeInput =
            window.matchMedia("(max-width: 768px)").matches ? inputMobile : input;
          activeInput?.focus({ preventScroll: true });
        });
      });
  });

  container
    .querySelectorAll<HTMLButtonElement>(".sb-language-tutorial-btn")
    .forEach((tutorialButton) => {
      tutorialButton.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeLanguageMenus();
        openAyeLanguageTutorial(readAyeInputLanguage());
      });
    });

  let showApps = false;

  function currentQuery(): string {
    return (
      input?.value ||
      inputMobile?.value ||
      new URLSearchParams(window.location.search).get("q") ||
      ""
    ).trim();
  }

  function submitQuery(q: string) {
    const trimmed = q.trim();
    if (!trimmed) return;

    const currentPath = window.location.pathname.toLowerCase();

    if (currentPath === "/search/images" || currentPath === "/search/images/") {
      window.location.href = `/search/images?q=${encodeURIComponent(trimmed)}`;
      return;
    }

    const normalized = normalizeQuery(trimmed);

    if (searchMap[normalized]) {
      window.location.href = searchMap[normalized];
    } else {
      window.location.href = `/search?q=${encodeURIComponent(trimmed)}`;
    }
  }

  function handleInput(el: HTMLInputElement) {
    if (!suggestionsBox) return;

    const value = el.value;

    suggestionsBox.innerHTML = renderSearchSuggestions(value);

    initSearchSuggestionsEvents(suggestionsBox, (selected) => {
      el.value = selected;
      submitQuery(selected);
    });
  }

  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    submitQuery(input?.value || "");
  });

  formMobile?.addEventListener("submit", (event) => {
    event.preventDefault();
    submitQuery(inputMobile?.value || "");
  });

  input?.addEventListener("input", () => handleInput(input));

  inputMobile?.addEventListener("input", () => handleInput(inputMobile));

  document.addEventListener("click", (event) => {
    closeLanguageMenus();

    if (wrapper && !wrapper.contains(event.target as Node) && suggestionsBox) {
      suggestionsBox.innerHTML = "";
    }

    if (appsBtn && !appsBtn.contains(event.target as Node) && appsPanel) {
      appsPanel.classList.add("hidden");
      showApps = false;
    }
  });

  appsBtn?.addEventListener("click", (event) => {
    event.stopPropagation();

    showApps = !showApps;

    appsPanel?.classList.toggle("hidden", !showApps);
  });

  const logo = container.querySelector("#sb-logo") as HTMLElement | null;

  const mobileLogo = container.querySelector(
    "#sb-mobile-logo",
  ) as HTMLElement | null;

  logo?.addEventListener("click", () => {
    window.location.href = "/";
  });

  mobileLogo?.addEventListener("click", () => {
    window.location.href = "/";
  });

  // Authentication-only addition. No layout wrappers are added.
  const openProfileDropdown = (anchor: HTMLElement) => {
    renderProfileDropdown(anchor, () => {});
  };

  profileIcon?.addEventListener("click", (event) => {
    event.stopPropagation();
    openProfileDropdown(profileIcon);
  });

  mobileProfileIcon?.addEventListener("click", (event) => {
    event.stopPropagation();
    openProfileDropdown(mobileProfileIcon);
  });

  signInBtn?.addEventListener("click", () => {
    window.location.href = "/login";
  });

  mobileSignInBtn?.addEventListener("click", () => {
    window.location.href = "/login";
  });

  container
    .querySelectorAll<HTMLButtonElement>(".cat-btn[data-category]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const category = button.dataset.category;

        const q = currentQuery();

        if (category === "aizone") {
          window.location.href = q
            ? `/aizone?q=${encodeURIComponent(q)}`
            : "/aizone";
          return;
        }

        if (category === "all") {
          window.location.href = q
            ? `/search?q=${encodeURIComponent(q)}`
            : "/search";
          return;
        }

        if (category === "images") {
          window.location.href = q
            ? `/search/images?q=${encodeURIComponent(q)}`
            : "/search/images";
          return;
        }

        container.querySelectorAll(".cat-btn").forEach((item) => {
          item.classList.remove("active");
        });

        button.classList.add("active");
      });
    });
}
